import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  getClientOrganization,
  inviteOrgClientUser,
  assignClientView,
  updateViewAssignment,
  removeViewAssignment,
  listAvailableClientViews,
  getOrgAuditLog,
  updateClientUserStatus,
  resendClientUserInvitation,
} from "../../../api/adminClientManagementApi";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import RequirePermission from "../../../auth/RequirePermission";
import StatusBadge from "../../../components/midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../../../components/midpoint/admin/users/adminUserFormatters";

const ROLES = ["CLIENT_OWNER", "CLIENT_MANAGER", "CLIENT_CONTRIBUTOR", "CLIENT_VIEWER"];

function tabFromPath(pathname, clientOrgId) {
  const base = `/admin/client-organizations/${clientOrgId}`;
  if (pathname.endsWith("/users")) return "users";
  if (pathname.endsWith("/views")) return "views";
  if (pathname.endsWith("/audit-log")) return "audit";
  if (pathname.endsWith("/edit")) return "edit";
  return "overview";
}

function AdminClientOrganizationHubPageContent() {
  const { clientOrgId } = useParams();
  const location = useLocation();
  const tab = tabFromPath(location.pathname, clientOrgId);

  const [detail, setDetail] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    role: "CLIENT_VIEWER",
    sendInvitation: true,
  });

  const [assignOpen, setAssignOpen] = useState(false);
  const [availableViews, setAvailableViews] = useState([]);
  const [assignForm, setAssignForm] = useState({
    clientViewId: "",
    role: "CLIENT_MANAGER",
    canManageCommunities: true,
    canManageCourses: true,
    canViewOpportunities: true,
    canManageSettings: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getClientOrganization(clientOrgId);
      setDetail(body);
      if (tab === "audit") {
        const auditBody = await getOrgAuditLog(clientOrgId, { pageSize: 50 });
        setAudit(auditBody.entries || []);
      }
    } catch (e) {
      setErrorMessage(e.message || "Failed to load organization.");
    } finally {
      setLoading(false);
    }
  }, [clientOrgId, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const org = detail?.organization;

  const handleInvite = async (event) => {
    event.preventDefault();
    try {
      const body = await inviteOrgClientUser(clientOrgId, inviteForm);
      setInviteOpen(false);
      setSuccessMessage(
        body.devToken
          ? `Invitation sent. Dev token: ${body.devToken}`
          : "Invitation sent."
      );
      load();
    } catch (e) {
      setErrorMessage(e.message || "Invite failed.");
    }
  };

  const openAssign = async () => {
    setAssignOpen(true);
    const body = await listAvailableClientViews(clientOrgId);
    setAvailableViews(body.clientViews || []);
  };

  const handleAssign = async (event) => {
    event.preventDefault();
    try {
      await assignClientView(clientOrgId, assignForm);
      setAssignOpen(false);
      setSuccessMessage("Client View assigned.");
      load();
    } catch (e) {
      setErrorMessage(e.message || "Assignment failed.");
    }
  };

  const handleDisableUser = async (user) => {
    if (!window.confirm(`Disable ${user.email}?`)) return;
    try {
      await updateClientUserStatus(user.clientUserId, { status: "DISABLED", reason: "Disabled by admin" });
      setSuccessMessage("User disabled.");
      load();
    } catch (e) {
      setErrorMessage(e.message || "Failed to disable user.");
    }
  };

  const handleResend = async (userId) => {
    try {
      const body = await resendClientUserInvitation(userId);
      setSuccessMessage(body.devToken ? `Invitation resent. Dev token: ${body.devToken}` : "Invitation resent.");
    } catch (e) {
      setErrorMessage(e.message || "Resend failed.");
    }
  };

  const handleRemoveAssignment = async (assignmentId, name) => {
    if (!window.confirm(`Remove assignment for ${name}?`)) return;
    try {
      await removeViewAssignment(clientOrgId, assignmentId);
      setSuccessMessage("Assignment removed.");
      load();
    } catch (e) {
      setErrorMessage(e.message || "Remove failed.");
    }
  };

  const handleTogglePerm = async (assignment, field) => {
    try {
      await updateViewAssignment(clientOrgId, assignment.assignmentId, {
        [field]: !assignment[field],
      });
      load();
    } catch (e) {
      setErrorMessage(e.message || "Update failed.");
    }
  };

  if (loading && !detail) {
    return (
      <AdminPageLayout>
        <AdminStatePanel message="Loading organization…" />
      </AdminPageLayout>
    );
  }

  if (!org) {
    return (
      <AdminPageLayout>
        <AdminStatePanel tone="error" message={errorMessage || "Organization not found."} />
      </AdminPageLayout>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", to: `/admin/client-organizations/${clientOrgId}` },
    { id: "users", label: "Users", to: `/admin/client-organizations/${clientOrgId}/users` },
    { id: "views", label: "Client Views", to: `/admin/client-organizations/${clientOrgId}/views` },
    { id: "audit", label: "Audit Log", to: `/admin/client-organizations/${clientOrgId}/audit-log` },
  ];

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={org.clientName}
        description={`/${org.slug} · ${org.status}`}
        actions={
          <>
            <Link to={`/admin/client-organizations/${clientOrgId}/edit`} className="cv-button-link">
              Edit
            </Link>
            <button type="button" className="midpoint-share-button" onClick={() => setInviteOpen(true)}>
              Invite User
            </button>
          </>
        }
      />

      <nav className="account-area-tabs panel-card">
        {tabs.map((t) => (
          <Link
            key={t.id}
            to={t.to}
            className={`account-area-tabs__link${tab === t.id ? " is-active" : ""}`}
          >
            {t.label}
          </Link>
        ))}
      </nav>

      {successMessage ? <p className="panel-card muted">{successMessage}</p> : null}
      {errorMessage ? <AdminStatePanel tone="error" message={errorMessage} /> : null}

      {tab === "overview" && (
        <section className="panel-card">
          <p>
            <StatusBadge label={org.status} tone="neutral" />
          </p>
          <p className="muted">
            Contact: {org.primaryContactName || "—"} · {org.primaryContactEmail || "—"}
          </p>
          <p className="muted">
            {org.assignedViewCount} views · {org.activeUserCount} active users ·{" "}
            {org.invitedUserCount} invited
          </p>
          <h3>Recent activity</h3>
          {detail.recentAudit?.length ? (
            <ul>
              {detail.recentAudit.map((e) => (
                <li key={e.auditLogId}>
                  <code>{e.eventType}</code> · {formatDateTime(e.createdAt)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">No audit activity has been recorded yet.</p>
          )}
        </section>
      )}

      {tab === "users" && (
        <section className="panel-card">
          {detail.users?.length ? (
            <AdminTableContainer>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail.users.map((u) => (
                    <tr key={u.clientUserId}>
                      <td>
                        {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                      </td>
                      <td>{u.email}</td>
                      <td>{u.role}</td>
                      <td>
                        <StatusBadge label={u.status} tone="neutral" />
                      </td>
                      <td>{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : "—"}</td>
                      <td>
                        {u.status === "INVITED" ? (
                          <button type="button" className="cv-button-link" onClick={() => handleResend(u.clientUserId)}>
                            Resend
                          </button>
                        ) : null}
                        {u.status !== "DISABLED" ? (
                          <button type="button" className="cv-button-link" onClick={() => handleDisableUser(u)}>
                            Disable
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="cv-button-link"
                            onClick={() =>
                              updateClientUserStatus(u.clientUserId, { status: "ACTIVE" }).then(load)
                            }
                          >
                            Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>
          ) : (
            <p className="muted">No client users have been invited yet.</p>
          )}
        </section>
      )}

      {tab === "views" && (
        <section className="panel-card">
          <p>
            <button type="button" className="midpoint-share-button" onClick={openAssign}>
              Assign Client View
            </button>
          </p>
          {detail.assignments?.length ? (
            <AdminTableContainer>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>View</th>
                    <th>Communities</th>
                    <th>Courses</th>
                    <th>Opportunities</th>
                    <th>Settings</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail.assignments.map((a) => (
                    <tr key={a.assignmentId}>
                      <td>
                        <strong>{a.viewName}</strong>
                        <div className="muted">{a.viewSlug}</div>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={a.canManageCommunities}
                          onChange={() => handleTogglePerm(a, "canManageCommunities")}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={a.canManageCourses}
                          onChange={() => handleTogglePerm(a, "canManageCourses")}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={a.canViewOpportunities}
                          onChange={() => handleTogglePerm(a, "canViewOpportunities")}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={a.canManageSettings}
                          onChange={() => handleTogglePerm(a, "canManageSettings")}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="cv-button-link"
                          onClick={() => handleRemoveAssignment(a.assignmentId, a.viewName)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableContainer>
          ) : (
            <p className="muted">No Client Views have been assigned to this organization.</p>
          )}
        </section>
      )}

      {tab === "audit" && (
        <section className="panel-card">
          {audit.length ? (
            <ul>
              {audit.map((e) => (
                <li key={e.auditLogId}>
                  <code>{e.eventType}</code> · {formatDateTime(e.createdAt)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">No audit activity has been recorded yet.</p>
          )}
        </section>
      )}

      {inviteOpen ? (
        <form className="panel-card client-community-form" onSubmit={handleInvite}>
          <h3>Invite client user</h3>
          <label className="admin-users-field">
            <span>Email *</span>
            <input type="email" required value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
          </label>
          <label className="admin-users-field">
            <span>First name</span>
            <input value={inviteForm.firstName} onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })} />
          </label>
          <label className="admin-users-field">
            <span>Last name</span>
            <input value={inviteForm.lastName} onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })} />
          </label>
          <label className="admin-users-field">
            <span>Role</span>
            <select value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-users-field client-portal-checkbox-field">
            <span>Send invitation email</span>
            <input
              type="checkbox"
              checked={inviteForm.sendInvitation}
              onChange={(e) => setInviteForm({ ...inviteForm, sendInvitation: e.target.checked })}
            />
          </label>
          <div className="client-view-card__actions">
            <button type="submit" className="midpoint-share-button">
              Send invite
            </button>
            <button type="button" className="cv-button-link" onClick={() => setInviteOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {assignOpen ? (
        <form className="panel-card client-community-form" onSubmit={handleAssign}>
          <h3>Assign Client View</h3>
          <label className="admin-users-field">
            <span>Client View *</span>
            <select
              required
              value={assignForm.clientViewId}
              onChange={(e) => setAssignForm({ ...assignForm, clientViewId: e.target.value })}
            >
              <option value="">Select…</option>
              {availableViews.map((v) => (
                <option key={v.clientViewId} value={v.clientViewId}>
                  {v.name} ({v.slug})
                </option>
              ))}
            </select>
          </label>
          <label className="admin-users-field client-portal-checkbox-field">
            <span>Manage communities</span>
            <input
              type="checkbox"
              checked={assignForm.canManageCommunities}
              onChange={(e) => setAssignForm({ ...assignForm, canManageCommunities: e.target.checked })}
            />
          </label>
          <label className="admin-users-field client-portal-checkbox-field">
            <span>Manage courses</span>
            <input
              type="checkbox"
              checked={assignForm.canManageCourses}
              onChange={(e) => setAssignForm({ ...assignForm, canManageCourses: e.target.checked })}
            />
          </label>
          <label className="admin-users-field client-portal-checkbox-field">
            <span>View opportunities</span>
            <input
              type="checkbox"
              checked={assignForm.canViewOpportunities}
              onChange={(e) => setAssignForm({ ...assignForm, canViewOpportunities: e.target.checked })}
            />
          </label>
          <label className="admin-users-field client-portal-checkbox-field">
            <span>Manage settings</span>
            <input
              type="checkbox"
              checked={assignForm.canManageSettings}
              onChange={(e) => setAssignForm({ ...assignForm, canManageSettings: e.target.checked })}
            />
          </label>
          <div className="client-view-card__actions">
            <button type="submit" className="midpoint-share-button">
              Assign
            </button>
            <button type="button" className="cv-button-link" onClick={() => setAssignOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </AdminPageLayout>
  );
}

const AdminClientOrganizationHubPage = () => (
  <RequirePermission permission="client_admin.view">
    <AdminClientOrganizationHubPageContent />
  </RequirePermission>
);

export default AdminClientOrganizationHubPage;
